import { useState } from 'react'
import { adminApplyFix, adminDataDoctor } from '../../lib/admin'
import type { DoctorFinding } from '../../lib/admin'
import Icon from '../../components/Icon'
import Modal from '../../components/Modal'
import { AdminPage } from './Admin'

/**
 * Data doctor.
 *
 * Every check corresponds to a bug that has actually happened in this dataset,
 * not a generic hygiene sweep — which is why most of them know how to fix
 * themselves, and the one that can't says so.
 */
export default function AdminDoctor() {
  const [findings, setFindings] = useState<DoctorFinding[] | null>(null)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<DoctorFinding | null>(null)
  const [result, setResult] = useState<string | null>(null)

  async function scan() {
    setScanning(true)
    setError(null)
    setResult(null)
    try {
      const data = await adminDataDoctor({})
      setFindings(data.findings)
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setScanning(false)
    }
  }

  return (
    <AdminPage
      title="Data doctor"
      lede="Scans every account for the specific ways this dataset goes wrong. Nothing is changed until you say so, and every fix is logged."
      actions={
        <button className="btn btn-primary" onClick={() => void scan()} disabled={scanning}>
          {scanning ? 'Scanning…' : findings ? 'Scan again' : 'Run scan'}
        </button>
      }
    >
      {error && (
        <p className="panel panel-pad t-caption" style={{ color: 'var(--destructive)' }}>
          {error}
        </p>
      )}

      {result && (
        <p
          className="panel panel-pad t-caption"
          style={{ color: 'var(--positive)', borderColor: 'var(--positive)' }}
        >
          {result}
        </p>
      )}

      {!findings && !scanning && !error && (
        <div className="panel panel-pad stack" style={{ gap: 8 }}>
          <span style={{ fontWeight: 600 }}>Nothing scanned yet</span>
          <p className="t-caption" style={{ maxWidth: '60ch' }}>
            The scan reads every tour, crew record, show and itinerary item across all
            accounts. It's read-only and safe to run any time — on a dataset this size it
            takes a few seconds.
          </p>
        </div>
      )}

      {findings && findings.length === 0 && (
        <div className="panel panel-pad stack" style={{ gap: 6 }}>
          <span style={{ fontWeight: 600, color: 'var(--positive)' }}>Nothing to fix</span>
          <p className="t-caption">No known problems found across any account.</p>
        </div>
      )}

      {findings && findings.length > 0 && (
        <div className="stack" style={{ gap: 12 }}>
          {findings.map((finding) => (
            <div
              key={finding.id}
              className="panel"
              style={{
                padding: 18,
                borderLeftWidth: 'var(--signal-rule)',
                borderLeftColor:
                  finding.severity === 'high' ? 'var(--destructive)' : 'var(--warning)',
              }}
            >
              <div className="spread" style={{ gap: 12, alignItems: 'flex-start' }}>
                <div className="stack" style={{ gap: 6, minWidth: 0 }}>
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700 }}>{finding.title}</span>
                    <span
                      className={
                        finding.severity === 'high' ? 'chip chip-warning' : 'chip'
                      }
                    >
                      {finding.severity}
                    </span>
                    <span className="chip">{finding.count} affected</span>
                  </div>
                  <p className="t-caption" style={{ maxWidth: '70ch' }}>
                    {finding.detail}
                  </p>
                  {finding.samples.length > 0 && (
                    <ul className="stack" style={{ gap: 2, listStyle: 'none', marginTop: 4 }}>
                      {finding.samples.map((sample, index) => (
                        <li key={index} className="t-spec-caption faint truncate">
                          {sample}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {finding.fixable ? (
                  <button
                    className="btn btn-secondary"
                    style={{ flex: 'none' }}
                    onClick={() => setConfirming(finding)}
                  >
                    Fix
                  </button>
                ) : (
                  <span className="chip" style={{ flex: 'none' }}>
                    Report only
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {confirming && (
        <FixConfirm
          finding={confirming}
          onCancel={() => setConfirming(null)}
          onDone={(changed) => {
            setConfirming(null)
            setResult(`Fixed ${changed} record${changed === 1 ? '' : 's'}.`)
            void scan()
          }}
        />
      )}
    </AdminPage>
  )
}

function FixConfirm({
  finding,
  onCancel,
  onDone,
}: {
  finding: DoctorFinding
  onCancel: () => void
  onDone: (changed: number) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Modal
      open
      width={480}
      title="Apply this fix"
      onClose={onCancel}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                const { changed } = await adminApplyFix({ findingId: finding.id })
                onDone(changed)
              } catch (caught) {
                setError((caught as Error).message)
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? 'Applying…' : `Fix ${finding.count}`}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="t-body">
          <strong>{finding.title}</strong> — {finding.count} record
          {finding.count === 1 ? '' : 's'} across all accounts.
        </p>
        <p className="t-caption">{finding.detail}</p>
        <p
          className="t-caption"
          style={{
            padding: '10px 13px',
            borderRadius: 'var(--radius-control)',
            background: 'color-mix(in srgb, var(--destructive) 10%, transparent)',
            color: 'var(--destructive)',
          }}
        >
          <Icon name="key" size={14} /> This writes to live customer data and Firestore has no
          undo. The action is recorded in the audit log with your account against it.
        </p>
        {error && (
          <p className="t-caption" style={{ color: 'var(--destructive)' }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}
